import { Platform } from 'react-native'
import { Buffer } from 'buffer'
import axios from 'axios'

// types
import {
  ImageMimeTypes,
  FileMimeTypes,
  ImageBase64,
  FileBase64,
  Form,
  FromsByCategory
} from '../types/onboardingTypes'

// constants
import { ALLOWED_EXTENSIONS_FALLBACK } from '../constants/initializeConstants'


/**
 * Groups an array of forms by their category description.
 *
 * @param {Form[]} forms - An array of Form objects.
 * @returns {FromsByCategory[]} An array of objects, each representing a category and containing an array of indexes for forms belonging to that category.
 */
const regroupFormsByCategory = (forms: Form[]): FromsByCategory[] => {
  return forms
    .reduce((acc, item, index) => {
      if (!item.isRequired) {
        return acc; 
      }

      const existingItem = acc.find(
        (d) => d.formCategoryDescription === item.formCategoryDescription
      );

      if (existingItem) {
        existingItem.formIds.push(index);
      } else {
        acc.push({
          formCategoryDescription: item.formCategoryDescription,
          formIds: [index],
        });
      }

      return acc;
    }, [])
    .map((item) => {
      return {
        ...item,
        formIds: item.formIds,
      };
    });
};

const base64ToBlob = (fileBase64: string): Blob => {
  const binaryData = Buffer.from(fileBase64, 'base64')
  const blobData = new Blob([binaryData], {
    type: 'application/octet-stream'
  })

  return blobData
}

const fetchFileAsBlob = async (uri: string): Promise<Blob> => {
  const response = await axios.get(uri, { responseType: 'blob' })
  const blob = response.data
  return blob
}

const mimeToExtension: { [key: string]: string } = {
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/gif': '.gif',
  'image/heic': '.heic',
  'application/pdf': '.pdf',
  'text/plain': '.txt',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    '.docx',
  'application/vnd.ms-excel': '.xsl',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xslx',
  'application/vnd.ms-powerpoint': '.ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    '.pptx'
  // add additional mappings as needed
}
const getFileExtensionFromMimeType = (mimeType: string): string => {
  const extension = mimeToExtension[mimeType]
  return extension ? extension : ''
}

/**
 * Exclude extensions
 *
 * @param allExtensions
 * @param excludedExtensions
 * @returns
 */
const excludeExtensions = (
  allExtensions: string[],
  excludedExtensions: string[]
): string[] => {
  return allExtensions.filter(
    (extension) => !excludedExtensions.includes(extension)
  )
}

/**
 * Converts single less-than characters (<) to entity.
 * Strips all tags. Remove line breaks, tabs and extra whitespace.
 *
 * @param name
 * @returns
 */
const sanitize = (name: string): string => {
  // convert special characters to their ASCII equivalents
  const asciiName = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

  // remove any HTML tags
  const strippedName = asciiName.replace(/<(?:.|\n)*?>/gm, '')

  // replace spaces and non-alphanumeric characters with hyphens
  const hyphenatedName = strippedName.replace(/[\s\W]+/g, '-')

  // convert the string to lowercase
  return hyphenatedName.toLowerCase()
}

/**
 * Get form data
 *
 * @param uri
 * @param formName
 * @returns
 */
const getFormData = async (
  uri: ImageBase64<ImageMimeTypes> | FileBase64<FileMimeTypes> | string,
  formName: string,
  allowedExtensions: string[]
): Promise<FormData> => {
  return new Promise(async (resolve, reject) => {
    // blob data
    let blobData: Blob
    blobData = await fetchFileAsBlob(uri)

    // use fallback allowed extension in case the api returns undefined value
    if (!allowedExtensions) allowedExtensions = ALLOWED_EXTENSIONS_FALLBACK

    // get the uploaded file extension
    const blobDataExtenion = getFileExtensionFromMimeType(blobData.type)

    // logs
    // console.log(
    //   'allowedExtensions from api or fallback',
    //   JSON.stringify(allowedExtensions)
    // )
    // console.log('blobData.type?', blobData.type)
    // console.log('blobDataExtenion?', blobDataExtenion)

    if (allowedExtensions.includes(blobDataExtenion)) {
      let formData = new FormData()
      type FormDataFileValue = {
        uri: string
        name: string
        type: string
      }
      let fileData: Blob | FormDataFileValue
      const fileName = `${sanitize(formName)}${getFileExtensionFromMimeType(
        blobData.type
      )}`

      if (Platform.OS === 'web') {
        fileData = blobData
        formData.append('', blobData, fileName)
        // console.log('uploading from web')
      } else {
        const fileData = {
          uri,
          name: fileName,
          type: blobData.type
        }

        formData.append('', JSON.parse(JSON.stringify(fileData)))
        // console.log('uploading from mobile')
      }

      resolve(formData)
    } else {
      reject(
        new Error(
          `Sorry, the file you're trying to upload has a file extension of "${blobDataExtenion}", which is not supported. We only support file extensions of "${allowedExtensions.join(
            ', '
          )}".`
        )
      )
    }
  })
}

/**
 * Get all form ids
 *
 * @param forms
 * @returns
 */
const getFormIds = (forms: Form[]): number[] => {
  return forms
    .filter((form) => form.isRead === false)
    .map((form) => form.essUserOnBoardingFormKey)
}

const getFormStatus = (
  forms: Form[],
  formId: number
): 'N' | 'P' | 'R' | 'C' => {
  const foundForm = forms.find(
    (form) => form.essUserOnBoardingFormKey === formId
  )

  if (!foundForm) return null

  return foundForm.formStatusValue
}

export {
  regroupFormsByCategory,
  base64ToBlob,
  fetchFileAsBlob,
  getFileExtensionFromMimeType,
  sanitize,
  getFormData,
  getFormIds,
  getFormStatus,
  excludeExtensions
}
